export interface RenewSubscriptionRequest {
  planId: string;
  idempotencyKey: string;
  tokenId?: string;
  useSavedCard?: boolean;
}

export interface SavedCard {
  brand: string;
  last4: string;
  expMonth: number;
  expYear: number;
}

export interface SubscriptionPlan {
  id: string;
  amount: number;
  currency: string;
  interval: string;
  name?: string;
}

const MONTHS_PER_YEAR = 12;

/** Adds calendar months in UTC; a day past the end of the target month moves back to its last day (31 Jan + 1 month = 28 Feb). */
function addMonthsUtc(base: Date, months: number): Date {
  const result = new Date(base.getTime());
  const dayOfMonth = base.getUTCDate();
  result.setUTCDate(1);
  result.setUTCMonth(result.getUTCMonth() + months);
  const lastDayOfTargetMonth = new Date(Date.UTC(result.getUTCFullYear(), result.getUTCMonth() + 1, 0)).getUTCDate();
  result.setUTCDate(Math.min(dayOfMonth, lastDayOfTargetMonth));
  return result;
}

/**
 * One renewal term after `base`: twelve months for a yearly plan, one month for anything else, the same reading as
 * `describePlan`. The app has only a monthly and an annual plan, so one term per plan is assumed; a plan with another
 * term (every 3 months, weekly) needs this and `describePlan` updated. Mirrors the server's `addSubscriptionTerm`.
 */
export function addSubscriptionTerm(base: Date, interval: string): Date {
  return addMonthsUtc(base, interval === 'year' ? MONTHS_PER_YEAR : 1);
}

/**
 * The expiry a renewal will set, which is also the next charge date: the later of the current expiry and now, plus one
 * term, so renewing early keeps the time left and renewing an expired subscription starts from now. Mirrors the server's
 * `calculateRenewalExpiry`; an expiry that is missing or unreadable counts as "not in the future", as it does there.
 */
export function calculateNextChargeDate(currentExpiry: Date | string | null | undefined, now: Date, interval: string): Date {
  const expiry = currentExpiry ? new Date(currentExpiry) : null;
  const stillRunning = !!expiry && !isNaN(expiry.getTime()) && expiry.getTime() > now.getTime();
  return addSubscriptionTerm(stillRunning ? expiry : now, interval);
}

/** Human readable plan name and renewal term, e.g. "Annual Plan" / "1 year". */
export function describePlan(plan: SubscriptionPlan | null | undefined): { name: string; term: string } | null {
  if (!plan) {
    return null;
  }
  return plan.interval === 'year'
    ? { name: 'Annual Plan', term: '1 year' }
    : { name: 'Monthly Plan', term: '1 month' };
}
